import { downloadBlob, formatFilename, RASTER_MIME, type RasterFormat } from '../core/export/download';
import { serialisePreset } from '../core/export/preset';
import { renderOne } from './engine';
import { ZipWriter } from './zip';
import type { Variant } from './types';

export type FolderStructure = 'flat' | 'technique' | 'word' | 'palette';

export interface BatchExportOptions {
  variants: Variant[];
  format: RasterFormat;
  quality: number;
  scale: number;
  filenameTemplate: string;
  folders: FolderStructure;
  includeManifest: boolean;
  includeCsv: boolean;
  includePresets: boolean;
  /** Re-render at `scale` instead of packing the gallery thumbnails. */
  fullResolution: boolean;
  onProgress?: (done: number, total: number, label: string) => void;
  signal?: { cancelled: boolean };
}

const EXTENSION: Record<RasterFormat, string> = { png: 'png', jpeg: 'jpg', webp: 'webp' };

function folderFor(variant: Variant, structure: FolderStructure): string {
  switch (structure) {
    case 'technique':
      return `${variant.meta.techniques.join('+') || 'text'}/`;
    case 'word':
      return `${formatFilename(variant.meta.word, {}) || 'text'}/`;
    case 'palette':
      return `${formatFilename(variant.meta.palette, {})}/`;
    default:
      return '';
  }
}

/**
 * Packs a batch into a ZIP.
 *
 * When `fullResolution` is on, each variant is re-rendered at export scale one at a
 * time and written straight into the archive. That is the whole point of the streaming
 * writer: the gallery keeps small thumbnails, and no more than a single full-size image
 * exists as bytes at any moment.
 */
export async function exportBatchZip(options: BatchExportOptions): Promise<{ blob: Blob; count: number }> {
  const zip = new ZipWriter();
  const rows: string[] = [];
  const manifest: Record<string, unknown>[] = [];
  const usable = options.variants.filter((v) => !v.hidden && (v.blob || options.fullResolution));
  const total = usable.length;

  rows.push(
    ['datei', 'index', 'seed', 'wort', 'font', 'techniken', 'palette', 'canvas', 'breite', 'hoehe', 'ms']
      .map(csvCell)
      .join(','),
  );

  let done = 0;
  for (const variant of usable) {
    if (options.signal?.cancelled) break;

    const tokens = {
      index: String(variant.meta.index).padStart(4, '0'),
      technique: variant.meta.techniques.join('+') || 'text',
      word: variant.meta.word,
      seed: variant.meta.seed,
      palette: variant.meta.palette,
      font: variant.meta.fontId,
      size: `${variant.doc.canvas.width}x${variant.doc.canvas.height}`,
    };
    const base = formatFilename(options.filenameTemplate, tokens);
    const name = `${folderFor(variant, options.folders)}${base}.${EXTENSION[options.format]}`;

    options.onProgress?.(done, total, name);

    let blob = variant.blob;
    let width = variant.width;
    let height = variant.height;

    if (options.fullResolution) {
      try {
        const rendered = await renderOne(variant.doc, options.scale, options.format, options.quality);
        blob = rendered.blob;
        width = rendered.width;
        height = rendered.height;
      } catch {
        // A variant that fails at export resolution is skipped, not fatal — the same
        // robustness rule the batch run itself follows.
        done++;
        continue;
      }
    }
    if (!blob) {
      done++;
      continue;
    }

    await zip.add(name, blob);

    if (options.includePresets) {
      await zip.add(
        `${folderFor(variant, options.folders)}${base}.typo.json`,
        JSON.stringify(serialisePreset(variant.doc, base), null, 2),
      );
    }

    manifest.push({
      file: name,
      ...variant.meta,
      width,
      height,
      renderMs: Math.round(variant.ms),
      document: variant.doc,
    });
    rows.push(
      [
        name,
        variant.meta.index,
        variant.meta.seed,
        variant.meta.word,
        variant.meta.fontId,
        variant.meta.techniques.join('+'),
        variant.meta.palette,
        variant.meta.canvas,
        width,
        height,
        Math.round(variant.ms),
      ]
        .map(csvCell)
        .join(','),
    );

    done++;
    // Yield so the progress bar repaints between images.
    if (done % 4 === 0) await new Promise((r) => setTimeout(r, 0));
  }

  if (options.includeManifest) {
    await zip.add(
      'manifest.json',
      JSON.stringify(
        {
          format: 'typo-studio-batch',
          version: 1,
          createdAt: new Date().toISOString(),
          count: manifest.length,
          imageFormat: RASTER_MIME[options.format],
          scale: options.fullResolution ? options.scale : 'thumbnail',
          variants: manifest,
        },
        null,
        2,
      ),
    );
  }
  // A UTF-8 BOM so spreadsheet apps open the umlauts correctly instead of guessing.
  if (options.includeCsv) await zip.add('index.csv', `﻿${rows.join('\n')}`);

  options.onProgress?.(total, total, 'Archiv wird geschrieben…');
  return { blob: zip.finish(), count: manifest.length };
}

function csvCell(value: string | number): string {
  const s = String(value);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Contact sheet: every variant on one labelled sheet.
 *
 * Useful for judging a run as a whole — a grid of thumbnails at a glance says more
 * about which direction is working than clicking through a gallery.
 */
export async function buildContactSheet(
  variants: Variant[],
  columns: number,
  cellSize: number,
  label: boolean,
): Promise<Blob> {
  const usable = variants.filter((v) => v.url && !v.hidden);
  if (usable.length === 0) throw new Error('Keine Varianten zum Anordnen.');

  const cols = Math.max(1, columns);
  const rows = Math.ceil(usable.length / cols);
  const labelHeight = label ? Math.round(cellSize * 0.14) : 0;
  const pad = Math.round(cellSize * 0.04);
  const cellW = cellSize;
  const cellH = cellSize + labelHeight;

  const canvas = document.createElement('canvas');
  canvas.width = cols * (cellW + pad) + pad;
  canvas.height = rows * (cellH + pad) + pad;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D-Kontext nicht verfügbar.');

  ctx.fillStyle = '#0b0b0e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = 'top';
  ctx.font = `${Math.round(labelHeight * 0.42)}px ui-monospace, monospace`;

  for (let i = 0; i < usable.length; i++) {
    const variant = usable[i];
    const cx = pad + (i % cols) * (cellW + pad);
    const cy = pad + Math.floor(i / cols) * (cellH + pad);

    const image = await loadImage(variant.url as string);
    // Fit inside the cell, preserving the variant's own aspect ratio — batches may mix
    // canvas formats, and stretching them would misrepresent every one of them.
    const fit = Math.min(cellW / image.width, cellSize / image.height);
    const w = image.width * fit;
    const h = image.height * fit;
    ctx.drawImage(image, cx + (cellW - w) / 2, cy + (cellSize - h) / 2, w, h);

    if (label) {
      ctx.fillStyle = '#9a9aab';
      ctx.fillText(
        `${String(variant.meta.index).padStart(3, '0')} · ${variant.meta.word} · ${variant.meta.seed}`,
        cx,
        cy + cellSize + 2,
        cellW,
      );
      ctx.fillStyle = '#6b6b7c';
      ctx.fillText(variant.meta.techniques.join(' + ') || 'Text', cx, cy + cellSize + 2 + labelHeight * 0.45, cellW);
    }
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Kontaktbogen fehlgeschlagen.'))), 'image/jpeg', 0.9);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Bild konnte nicht geladen werden.'));
    image.src = url;
  });
}

export { downloadBlob };
